import { useCallback, useEffect, useRef, useState } from 'react';
import { isNotFoundError, updateOfficeExport } from '../services/userService';

/** Zustand des Office-Export-Schalters. */
export interface OfficeExportToggleState {
  /** true, waehrend eine Aenderung an das Backend gesendet wird. */
  readonly saving: boolean;
  /** Deutsche Fehlermeldung des letzten fehlgeschlagenen Versuchs oder null. */
  readonly error: string | null;
  /** Stoesst die Aenderung an (wird waehrend `saving` ignoriert). */
  toggle(enabled: boolean): void;
}

/** Fehlermeldung, wenn der Benutzer nicht (mehr) existiert (HTTP 404). */
const NOT_FOUND_MESSAGE = 'Der Benutzer wurde nicht gefunden.';

/** Fehlermeldung fuer alle uebrigen Fehlerfaelle. */
const GENERIC_ERROR_MESSAGE = 'Der Office-Export konnte nicht geändert werden.';

/**
 * Kapselt PUT /api/users/{id}/office-export: sendet die Aenderung, meldet
 * den Ladezustand und uebernimmt den vom Backend BESTAETIGTEN Wert per
 * `onApplied` in die Stammdaten (siehe useUserDetail.applyOfficeExport).
 *
 * Kein Caching (Projektstandard): Jeder Toggle-Klick loest einen eigenen
 * Request aus.
 *
 * Beim Wechsel von `userId` werden Fehler und `saving` zurueckgesetzt und
 * ein noch laufender Request abgebrochen — sonst koennte eine verspaetete
 * Antwort fuer den VORHERIGEN Benutzer die Stammdaten des NEU gewaehlten
 * Benutzers veraendern. Ein Toggle waehrend `saving` wird ignoriert, damit
 * kein zweiter Request ueberlappt. Abgebrochene Requests werden still
 * ignoriert (KEIN console.error).
 *
 * Beim Unmount der Komponente wird ein noch laufender Request ebenfalls
 * abgebrochen (Cleanup-Funktion des Effekts). Das ist rein client-seitig:
 * eine vom Backend bereits verarbeitete Mutation (der Office-Export-Wert
 * wurde in der mvgen-DB bereits geaendert) wird dadurch NICHT rueckgaengig
 * gemacht — das ist akzeptiert, da hier lediglich verhindert wird, dass
 * eine verspaetete Antwort noch versucht, State einer nicht mehr
 * gemounteten Komponente zu setzen.
 */
export function useOfficeExportToggle(
  userId: string | null,
  onApplied: (enabled: boolean) => void
): OfficeExportToggleState {
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const savingRef = useRef<boolean>(false);

  useEffect(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    savingRef.current = false;
    setSaving(false);
    setError(null);

    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, [userId]);

  const toggle = useCallback(
    (enabled: boolean): void => {
      if (userId === null || savingRef.current) {
        return;
      }
      const controller = new AbortController();
      controllerRef.current = controller;
      savingRef.current = true;
      setSaving(true);
      setError(null);
      const applyOnSuccess = onApplied;
      updateOfficeExport(userId, enabled, controller.signal)
        .then((confirmed) => {
          if (controller.signal.aborted) {
            return;
          }
          savingRef.current = false;
          setSaving(false);
          applyOnSuccess(confirmed);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) {
            return;
          }
          savingRef.current = false;
          setSaving(false);
          setError(isNotFoundError(caught) ? NOT_FOUND_MESSAGE : GENERIC_ERROR_MESSAGE);
        });
    },
    [userId, onApplied]
  );

  return { saving, error, toggle };
}

export default useOfficeExportToggle;
